import { useMemo, useRef, useState } from 'react'
import type { Options } from 'qr-code-styling'
import QrCanvas from './qr/QrCanvas'
import type { QrCanvasHandle } from './qr/QrCanvas'
import PlaquePreview from './qr/PlaquePreview'
import type { PlaquePreviewHandle } from './qr/PlaquePreview'
import { presets } from './qr/presets'
import type { QrStyleState } from './qr/types'
import { downloadStl, downloadDxf } from './qr/stlExport'
import { downloadStencilStl } from './qr/stencilExport'

// Index of the 3D-print preset in the presets array.
const PLAQUE_PRESET_INDEX = 0

// The size rendered in the preview — always small regardless of export size.
const PREVIEW_SIZE = 220

// Gallery URLs in QR codes always point to production, regardless of where the
// admin panel is running (e.g. localhost in dev).
export const GALLERY_BASE_URL = 'https://h3r.no'

const dotTypes = [
  { value: 'rounded', label: 'Rundet' },
  { value: 'dots', label: 'Prikker' },
  { value: 'classy-rounded', label: 'Klassisk' },
  { value: 'square', label: 'Firkantet' },
  { value: 'extra-rounded', label: 'Ekstra rundet' },
] as const

const errorLevels = [
  { value: 'L', label: 'L', description: 'Enklest — anbefalt' },
  { value: 'M', label: 'M', description: 'Moderat' },
  { value: 'Q', label: 'Q', description: 'Høy' },
  { value: 'H', label: 'H', description: 'Maks — for logo' },
] as const

const shapes = [
  { value: 'square', label: 'Firkant' },
  { value: 'circle', label: 'Sirkel' },
] as const

type Props = {
  galleryUrl: string
  galleryName: string | null
  onClose: () => void
}

// Derive a single "corner color" from the style — whichever color the corners are using.
function cornerColor(style: Omit<QrStyleState, 'url'>): string {
  return style.cornersSquareColor
}

// Apply a corner color to all corner fields.
function applyCornerColor(
  style: Omit<QrStyleState, 'url'>,
  color: string,
): Omit<QrStyleState, 'url'> {
  return {
    ...style,
    cornersSquareColor: color,
    cornersSquareGradientStartColor: color,
    cornersSquareGradientEndColor: color,
    cornersDotColor: color,
  }
}

function toFilename(name: string | null): string {
  if (!name) return 'qr-code'
  return name.trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 60) || 'qr-code'
}

export default function QrEditorModal({ galleryUrl, galleryName, onClose }: Props) {
  const [presetIndex, setPresetIndex] = useState(0)
  const [style, setStyle] = useState<Omit<QrStyleState, 'url'>>({ ...presets[0].config })
  const qrRef = useRef<QrCanvasHandle>(null)
  const plaqueRef = useRef<PlaquePreviewHandle>(null)

  const filename = toFilename(galleryName)

  const handleStlDownload = () => {
    downloadStl(galleryUrl, filename)
  }

  const setField = <K extends keyof Omit<QrStyleState, 'url'>>(
    key: K,
    value: Omit<QrStyleState, 'url'>[K],
  ) => {
    setStyle((prev) => ({ ...prev, [key]: value }))
  }

  const applyPreset = (index: number) => {
    setPresetIndex(index)
    setStyle({ ...presets[index].config })
  }

  // Build options for the preview (small, fixed size).
  const previewOptions = useMemo<Options>(() => buildOptions(style, galleryUrl, PREVIEW_SIZE), [style, galleryUrl])

  // Build options for the export instance (full size from preset).
  const exportOptions = useMemo<Options>(() => buildOptions(style, galleryUrl, style.size), [style, galleryUrl])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm flex flex-col overflow-y-auto max-h-[92dvh] sm:max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div>
            <h2 className="text-sm font-semibold text-white">QR-kode</h2>
            {galleryName && (
              <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[220px]">{galleryName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-white transition-colors rounded-lg -mr-1"
            aria-label="Lukk"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Preview */}
        <div className="flex flex-col items-center gap-2 py-6 bg-gray-950 border-b border-gray-800">
          <div className="rounded-lg overflow-hidden" style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}>
            {presetIndex === PLAQUE_PRESET_INDEX
              ? <PlaquePreview ref={plaqueRef} url={galleryUrl} size={PREVIEW_SIZE} />
              : <QrCanvas ref={qrRef} options={previewOptions} exportOptions={exportOptions} />
            }
          </div>
          <p className="text-xs text-gray-600 font-mono truncate max-w-[260px] px-4">{galleryUrl}</p>
        </div>

        {/* Presets */}
        <div className="flex border-b border-gray-800">
          {presets.map((preset, index) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => applyPreset(index)}
              className={`
                flex-1 py-2.5 text-xs font-medium transition-colors
                ${index === presetIndex
                  ? 'text-white bg-gray-800'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'}
                ${index > 0 ? 'border-l border-gray-800' : ''}
              `}
            >
              {preset.name}
            </button>
          ))}
        </div>

        {/* Controls */}
        {presetIndex === PLAQUE_PRESET_INDEX ? (
          <div className="px-5 py-4 space-y-1">
            <p className="text-xs text-gray-600">
              Versjon 1 (21×21) · Feilkorrigering L · Alphanumerisk modus
            </p>
          </div>
        ) : (
          <div className="p-5 space-y-4">

            {/* Shape + Corner style */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <span className="text-xs text-gray-500">Form</span>
                <div className="flex gap-1">
                  {shapes.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setField('shape', s.value)}
                      className={`
                        flex-1 py-1.5 text-xs rounded-md border transition-colors
                        ${style.shape === s.value
                          ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                          : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-300'}
                      `}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-xs text-gray-500">Hjørner</span>
                <div className="flex gap-1">
                  {([
                    { value: 'extra-rounded', label: 'Runde' },
                    { value: 'square', label: 'Firkant' },
                  ] as const).map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => {
                        setField('cornersSquareType', c.value)
                        setField('cornersDotType', c.value === 'square' ? 'square' : 'dot')
                      }}
                      className={`
                        flex-1 py-1.5 text-xs rounded-md border transition-colors
                        ${style.cornersSquareType === c.value
                          ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                          : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-300'}
                      `}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Dot style */}
            <div className="flex flex-col gap-2">
              <span className="text-xs text-gray-500">Prikke-stil</span>
              <select
                value={style.dotsType}
                onChange={(e) => setField('dotsType', e.target.value as QrStyleState['dotsType'])}
                className="w-full bg-gray-800 border border-gray-700 text-white text-base rounded-md px-2.5 py-1.5 focus:outline-none focus:border-blue-500"
              >
                {dotTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Error correction */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-500">Feilkorrigering</span>
                <span className="text-xs text-gray-600">Lavere = enklere kode</span>
              </div>
              <div className="flex gap-1">
                {errorLevels.map((l) => (
                  <button
                    key={l.value}
                    type="button"
                    title={l.description}
                    onClick={() => setField('errorCorrection', l.value)}
                    className={`
                      flex-1 py-1.5 text-xs rounded-md border font-mono transition-colors
                      ${style.errorCorrection === l.value
                        ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                        : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-300'}
                    `}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-600">
                {errorLevels.find((l) => l.value === style.errorCorrection)?.description}
                {' — '}
                {style.errorCorrection === 'L'
                  ? 'færrest moduler, lettere å skanne'
                  : style.errorCorrection === 'H'
                  ? 'tetteste mønster, bruk bare med logo'
                  : 'mer redundans, tettere mønster'}
              </p>
            </div>

            {/* Color row */}
            <div className="grid grid-cols-3 gap-3">
              <ColorSwatch
                label="Prikker"
                value={style.dotsColor}
                onChange={(v) => {
                  setField('dotsColor', v)
                  setField('dotsGradientStartColor', v)
                  setField('dotsGradientEndColor', v)
                }}
              />
              <ColorSwatch
                label="Hjørner"
                value={cornerColor(style)}
                onChange={(v) => setStyle((prev) => applyCornerColor(prev, v))}
              />
              <ColorSwatch
                label="Bakgrunn"
                value={style.backgroundColor.slice(0, 7) || '#ffffff'}
                onChange={(v) => {
                  setField('backgroundColor', v)
                  setField('backgroundGradientStartColor', v)
                  setField('backgroundGradientEndColor', v)
                }}
              />
            </div>

          </div>
        )}

        {/* Download */}
        <div className="flex flex-col gap-2 px-5 pb-5">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => presetIndex === PLAQUE_PRESET_INDEX
                ? plaqueRef.current?.download('svg', filename)
                : qrRef.current?.download('svg', filename)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              SVG
            </button>
            <button
              type="button"
              onClick={() => presetIndex === PLAQUE_PRESET_INDEX
                ? plaqueRef.current?.download('png', filename)
                : qrRef.current?.download('png', filename)}
              className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg transition-colors"
            >
              PNG
            </button>
          </div>
          <button
            type="button"
            onClick={handleStlDownload}
            className="w-full flex items-center justify-center gap-1.5 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg transition-colors border border-gray-700"
            title="50×50mm, 3mm total height (1.5mm base + 1.5mm modules)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
            </svg>
            STL for 3D-print
          </button>
          <button
            type="button"
            onClick={() => downloadDxf(galleryUrl, filename)}
            className="w-full flex items-center justify-center gap-1.5 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg transition-colors border border-gray-700"
            title="2D DXF for CAD — importer og ekstruder selv. 50×50mm, én lukket kontur per QR-kropp."
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zm0 9.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zm9.75-9.75A2.25 2.25 0 0115.75 3.75H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zm0 9.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
            </svg>
            DXF for CAD
          </button>
          <button
            type="button"
            onClick={() => downloadStencilStl(galleryUrl, filename)}
            className="w-full flex items-center justify-center gap-1.5 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg transition-colors border border-gray-700"
            title="Sjablong for spraymaling: 50×50mm QR + 8mm ramme, 1.2mm tykk. Runde hull — farger og stil påvirker ikke sjablongen. Kan skaleres opp i sliceren for større flater."
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
            </svg>
            Sjablong for 3D-print
          </button>
        </div>

      </div>
    </div>
  )
}

function ColorSwatch({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-xs text-gray-500">{label}</span>
      <label className="cursor-pointer">
        <div
          className="w-8 h-8 rounded-lg border-2 border-gray-700 hover:border-gray-500 transition-colors"
          style={{ backgroundColor: value }}
        />
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
        />
      </label>
    </div>
  )
}

function buildOptions(
  style: Omit<QrStyleState, 'url'>,
  data: string,
  size: number,
): Options {
  const dotsGradient = style.dotsGradientEnabled
    ? {
        type: style.dotsGradientType,
        rotation: (style.dotsGradientRotation * Math.PI) / 180,
        colorStops: [
          { offset: 0, color: style.dotsGradientStartColor },
          { offset: 1, color: style.dotsGradientEndColor },
        ],
      }
    : undefined

  const cornersSquareGradient = style.cornersSquareGradientEnabled
    ? {
        type: style.cornersSquareGradientType,
        rotation: (style.cornersSquareGradientRotation * Math.PI) / 180,
        colorStops: [
          { offset: 0, color: style.cornersSquareGradientStartColor },
          { offset: 1, color: style.cornersSquareGradientEndColor },
        ],
      }
    : undefined

  const backgroundGradient = style.backgroundGradientEnabled
    ? {
        type: style.backgroundGradientType,
        rotation: (style.backgroundGradientRotation * Math.PI) / 180,
        colorStops: [
          { offset: 0, color: style.backgroundGradientStartColor },
          { offset: 1, color: style.backgroundGradientEndColor },
        ],
      }
    : undefined

  return {
    width: size,
    height: size,
    type: 'canvas',
    shape: style.shape,
    data,
    margin: style.margin,
    qrOptions: { errorCorrectionLevel: style.errorCorrection },
    image: style.logoEnabled ? style.logoImage : undefined,
    imageOptions: {
      hideBackgroundDots: style.logoHideBackgroundDots,
      imageSize: style.logoSize,
      margin: style.logoMargin,
    },
    dotsOptions: {
      type: style.dotsType,
      color: style.dotsColor,
      gradient: dotsGradient,
      roundSize: style.dotsRoundSize,
    },
    cornersSquareOptions: {
      type: style.cornersSquareType,
      color: style.cornersSquareColor,
      gradient: cornersSquareGradient,
    },
    cornersDotOptions: {
      type: style.cornersDotType,
      color: style.cornersDotColor,
    },
    backgroundOptions: {
      color: style.backgroundColor,
      gradient: backgroundGradient,
    },
  }
}
